import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

// Hover intent
// - First open: short pause so menus don't flash open when the pointer just crosses the header.
// - Switching while a dropdown is open: if the pointer is heading down/diagonally (towards the
//   open dropdown) wait longer, so passing over neighbouring items doesn't switch menus;
//   moving sideways along the bar switches quickly.
const ACTIVATE_DELAY = 140;
const SWITCH_DELAY_SIDEWAYS = 80;
const SWITCH_DELAY_TOWARDS_MENU = 260;
const DEACTIVATE_DELAY = 350;

// Fit-to-width: shrink the gap between top-level items first, then the text,
// so every item stays on one line instead of moving into "More".
const FIT_MIN_GAP = 12;
const FIT_MIN_FONT = 11;
const FIT_SAFETY = 8;

/** Width of an element's actual contents (ignores any stretched empty space). */
const contentWidth = (element) => {
  const range = document.createRange();
  range.selectNodeContents(element);
  return range.getBoundingClientRect().width;
};

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  /** @type {ResizeObserver | null} */
  #fitObserver = null;

  /** @type {{ font: number, gap: number } | null} */
  #fitBase = null;

  #fitFrame = 0;

  /** Recent pointer positions, used to tell which way the pointer is travelling */
  /** @type {{ x: number, y: number, t: number }[]} */
  #trail = [];

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.addEventListener(
      'pointermove',
      (event) => {
        this.#trail.push({ x: event.clientX, y: event.clientY, t: event.timeStamp });
        if (this.#trail.length > 6) this.#trail.shift();
      },
      { signal: this.#abortController.signal, passive: true }
    );

    onDocumentLoaded(this.#preloadImages);
    this.#setupFit();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#fitObserver?.disconnect();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Whether the pointer is currently travelling downwards (towards an open dropdown).
   * @returns {boolean}
   */
  #headingTowardsMenu() {
    if (this.#trail.length < 2) return false;
    const first = this.#trail[0];
    const last = this.#trail[this.#trail.length - 1];
    if (last.t - first.t > 300) return false; // stale movement
    const dx = Math.abs(last.x - first.x);
    const dy = last.y - first.y;
    return dy > 2 && dy >= dx * 0.35;
  }

  /**
   * Activate the selected menu item, with hover intent (see the delays at the top of this file).
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#cancelPendingActivation();

    const isKeyboard = event.type === 'focus' || event.type === 'focusin';

    if (isKeyboard) {
      this.#activateHandler(event);
    } else if (!this.#state.activeItem) {
      this.#debouncedActivateHandler(event);
    } else if (this.#headingTowardsMenu()) {
      this.#debouncedSwitchSlow(event);
    } else {
      this.#debouncedSwitchFast(event);
    }
  };

  #cancelPendingActivation() {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedSwitchFast.cancel();
    this.#debouncedSwitchSlow.cancel();
  }

  /**
   * Activate the selected menu item
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight =
      submenu === this.overflowMenu
        ? Math.max(submenu.offsetHeight, overflowMenuHeight)
        : (submenu?.offsetHeight ?? 0);

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);
  #debouncedSwitchFast = debounce(this.#activateHandler, SWITCH_DELAY_SIDEWAYS);
  #debouncedSwitchSlow = debounce(this.#activateHandler, SWITCH_DELAY_TOWARDS_MENU);

  /**
   * Leaving any menu item: cancel a pending switch and start the close timer for whatever is
   * open. Entering the open dropdown (or another item) cancels the close again.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#cancelPendingActivation();

    if (!(event.target instanceof Element)) return;

    this.#debouncedDeactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   */
  #debouncedDeactivate = debounce(() => this.#deactivate(), DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /* ---------- Fit-to-width ---------- */

  #setupFit() {
    const row = this.closest('.header__columns');
    if (!row || !('ResizeObserver' in window)) return;

    const schedule = () => {
      cancelAnimationFrame(this.#fitFrame);
      this.#fitFrame = requestAnimationFrame(this.#fit);
    };

    this.#fitObserver = new ResizeObserver(schedule);
    this.#fitObserver.observe(row);
    window.addEventListener('resize', schedule, { signal: this.#abortController.signal });
    document.fonts?.ready.then(schedule);
    schedule();
  }

  #clearFit() {
    this.style.removeProperty('--mujo-menu-font');
    this.style.removeProperty('--mujo-menu-gap');
  }

  #fit = () => {
    const row = this.closest('.header__columns');
    const column = this.closest('.header__column');
    const titles = [...this.querySelectorAll('.menu-list__list-item:not([slot="more"]) .menu-list__link-title')];
    if (!row || !column || !titles.length || window.innerWidth < 750) {
      this.#clearFit();
      return;
    }

    const link = titles[0].closest('.menu-list__link') || titles[0];

    if (!this.#fitBase) {
      this.#clearFit();
      const list = this.refs.overflowMenu?.shadowRoot?.querySelector('[part="list"]');
      this.#fitBase = {
        font: parseFloat(getComputedStyle(link).fontSize) || 14,
        gap: list ? parseFloat(getComputedStyle(list).columnGap) || 20 : 20,
      };
    }
    const { font: baseFont, gap: baseGap } = this.#fitBase;

    const styles = getComputedStyle(link);
    const canvas = HeaderMenu.#canvas || (HeaderMenu.#canvas = document.createElement('canvas'));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.font = `${styles.fontStyle} ${styles.fontWeight} ${baseFont}px ${styles.fontFamily}`;
    const upper = styles.textTransform === 'uppercase';
    const textWidth = titles.reduce((sum, el) => {
      const text = (el.textContent || '').trim();
      return sum + ctx.measureText(upper ? text.toUpperCase() : text).width;
    }, 0);
    const gaps = titles.length - 1;

    const rowStyles = getComputedStyle(row);
    const columns = [...row.children].filter((el) => el.getClientRects().length);
    const others = columns.filter((el) => el !== column).reduce((sum, el) => sum + contentWidth(el), 0);
    const columnGap = parseFloat(rowStyles.columnGap) || 0;
    const available =
      row.clientWidth -
      parseFloat(rowStyles.paddingLeft) -
      parseFloat(rowStyles.paddingRight) -
      others -
      columnGap * Math.max(0, columns.length - 1) -
      FIT_SAFETY;

    if (textWidth + gaps * baseGap <= available) {
      this.#clearFit();
      return;
    }

    let gap = Math.max(FIT_MIN_GAP, (available - textWidth) / Math.max(1, gaps));
    let font = baseFont;

    if (textWidth + gaps * gap > available) {
      gap = FIT_MIN_GAP;
      font = Math.max(FIT_MIN_FONT, ((available - gaps * gap) * baseFont) / textWidth);
    }

    this.style.setProperty('--mujo-menu-gap', `${Math.floor(gap)}px`);
    this.style.setProperty('--mujo-menu-font', `${Math.floor(font * 10) / 10}px`);
  };

  /** @type {HTMLCanvasElement | null} */
  static #canvas = null;
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}